import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function VoiceRecorder({ onParsed, hasExistingData = false }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        await handleUpload(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch (e) {
      toast.error("No se pudo acceder al micrófono");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const handleUpload = async (blob) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      const tRes = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcript = tRes.data.transcript || "";
      if (!transcript.trim()) {
        toast.error("No se detectó voz. Inténtalo de nuevo.");
        return;
      }
      toast.success("Transcripción lista, analizando...");
      const pRes = await api.post("/voice/parse", { transcript });
      onParsed?.(pRes.data, transcript);
      toast.success("Campos rellenados automáticamente");
    } catch (e) {
      console.error(e);
      toast.error("Error procesando audio");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl p-5 text-white gradient-sky">
      <div className="relative z-10 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#FFC740]">
            <Sparkles size={12} /> Entrada por voz
          </div>
          <h3 className="font-display font-extrabold text-xl mt-1 leading-tight">
            {hasExistingData ? "Añade más datos" : "Dicta tu parte"}
          </h3>
          <p className="text-xs text-white/80 mt-1 max-w-xs leading-relaxed">
            {hasExistingData
              ? "Pulsa y dicta lo que falte (operarios, materiales…). Se añadirá a lo ya escrito."
              : "Pulsa, narra los detalles y la IA rellenará el formulario automáticamente."}
          </p>
        </div>

        {!recording && !processing && (
          <button
            type="button"
            onClick={start}
            data-testid="voice-record-button"
            className="shrink-0 w-20 h-20 rounded-full gradient-sun shadow-[0_10px_30px_-8px_rgba(245,130,32,0.7)]
                       flex items-center justify-center hover:scale-105 transition-transform"
            aria-label="Iniciar grabación"
          >
            <Mic size={32} strokeWidth={2.5} className="text-white" />
          </button>
        )}

        {recording && (
          <button
            type="button"
            onClick={stop}
            data-testid="voice-stop-button"
            className="shrink-0 w-20 h-20 rounded-full bg-red-500 voice-pulse
                       flex items-center justify-center"
            aria-label="Parar grabación"
          >
            <Square size={26} strokeWidth={2.5} fill="white" className="text-white" />
          </button>
        )}

        {processing && (
          <div className="shrink-0 w-20 h-20 rounded-full bg-[#FFC740] flex items-center justify-center">
            <Loader2 size={32} className="animate-spin text-[#0B2A4A]" />
          </div>
        )}
      </div>

      {recording && (
        <div className="relative z-10 mt-4 flex items-center gap-2 text-sm">
          <span className="inline-block w-2 h-2 bg-[#FFC740] rounded-full animate-pulse" />
          <span className="font-mono font-semibold" data-testid="voice-timer">
            {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
          </span>
          <span className="text-white/70 text-xs">Grabando — habla con claridad</span>
        </div>
      )}
      {processing && (
        <div className="relative z-10 mt-4 text-sm text-white/85" data-testid="voice-processing">
          Transcribiendo y analizando...
        </div>
      )}

      {/* decorative */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -left-10 w-56 h-56 rounded-full opacity-25"
        style={{ background: "radial-gradient(closest-side, #FFC740, transparent 70%)" }}
      />
    </div>
  );
}
